import React, { useEffect, useState } from "react";
import { TIER_META } from "@/components/TierBadge";
import api from "@/lib/api";

// Twin progress bars showing "X/N schede" and "X/N immobili" against the
// user's tier quota. Silent when unlimited (limit === -1). Refreshes on
// `refreshKey` change so parents can force a reload after mutations.
const QuotaProgress = ({ refreshKey = 0, className = "" }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/users/me/quota");
        if (!cancelled) setData(r.data);
      } catch (err) {
        console.warn("[QuotaProgress] fetch failed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (!data) return null;
  const meta = TIER_META[data.tier] || TIER_META.demo;
  const Bar = ({ used, limit, label, testId }) => {
    const unlimited = limit === -1;
    const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
    const hot = !unlimited && used >= limit;
    const warm = !unlimited && !hot && pct >= 80;
    const barColor = hot ? "#FF3B30" : warm ? "#F59E0B" : meta.color;
    return (
      <div className="min-w-0" data-testid={testId}>
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
          <span>{label}</span>
          <span className="font-mono-num text-slate-700">
            {used}<span className="text-slate-400"> / {unlimited ? "∞" : limit}</span>
          </span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: unlimited ? "100%" : `${pct}%`, backgroundColor: unlimited ? "#34C759" : barColor, opacity: unlimited ? 0.35 : 1 }}
          />
        </div>
      </div>
    );
  };
  return (
    <div className={`ios-card px-4 py-3 flex items-center gap-4 ${className}`} data-testid="quota-progress">
      <div className="shrink-0 flex items-center gap-2">
        <span
          className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-white shadow-sm"
          style={{ border: `2px solid ${meta.color}` }}
          title={meta.label}
          data-testid={`quota-tier-icon-${data.tier}`}
        >
          <img
            src={`/board-icons/${meta.icon}.png`}
            alt=""
            aria-hidden="true"
            className="w-7 h-7 object-contain"
            style={{ filter: `drop-shadow(0 1px 2px ${meta.color}80)` }}
          />
        </span>
        <div className="leading-tight">
          <div className="text-[9px] uppercase tracking-[0.24em] text-slate-500 font-semibold">Piano</div>
          <div className="text-sm font-bold" style={{ color: meta.color }}>{meta.label}</div>
        </div>
      </div>
      <div className="flex-1 grid grid-cols-2 gap-4 min-w-0">
        <Bar used={data.boards.used} limit={data.boards.limit} label="Schede" testId="quota-boards" />
        <Bar used={data.properties.used} limit={data.properties.limit} label="Immobili" testId="quota-properties" />
      </div>
    </div>
  );
};

export default QuotaProgress;
